
import { Component } from "@/utils/types/componentTypes";
import { ComponentGroup } from "./ComponentGroup";
import { RectangleHorizontal, Type, User } from "lucide-react";

interface ComponentsListProps {
  components: Component[];
  loading: boolean;
  searchTerm: string;
  organizationFilter: string;
  expandedGroups: { [key: string]: boolean };
  onToggleGroup: (groupName: string) => void;
  onDragStart: (event: React.DragEvent, component: Component) => void;
}

export function ComponentsList({
  components,
  loading,
  searchTerm,
  organizationFilter,
  expandedGroups,
  onToggleGroup,
  onDragStart
}: ComponentsListProps) {
  const filteredComponents = components.filter(component => {
    const matchesSearch = component.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOrganization = organizationFilter === "todas" || component.id_organization === organizationFilter;
    return matchesSearch && matchesOrganization;
  });

  // Agrupar componentes por categoria (group_name do tipo de componente)
  const groupedComponents = filteredComponents.reduce<{ [key: string]: Component[] }>((acc, component) => {
    const groupName = component.component_types?.group_name || 'Sem Categoria';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(component);
    return acc;
  }, {});

  // Ordenar componentes dentro de cada grupo alfabeticamente
  Object.keys(groupedComponents).forEach(groupName => {
    groupedComponents[groupName].sort((a, b) => 
      a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
    );
  });

  // Ordenar as categorias alfabeticamente
  const sortedGroupNames = Object.keys(groupedComponents).sort((a, b) => 
    a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
  );

  if (loading) {
    return (
      <div className="p-4 text-center text-sidebar-foreground/70">
        Carregando componentes...
      </div>
    );
  }

  if (Object.keys(groupedComponents).length === 0) {
    return (
      <div className="p-4 text-center text-sidebar-foreground/70">
        {searchTerm || organizationFilter !== "todas" ? "Nenhum componente encontrado" : "Nenhum componente disponível"}
      </div>
    );
  }

  // Elementos fixos "Outros elementos"
  const fixedElements: Component[] = [
    {
      id: 'moldura-element',
      name: 'Moldura',
      component_types: { 
        id: 'moldura-type',
        name: 'Moldura', 
        icon: null,
        group_name: 'Outros'
      },
      id_organization: 'system',
      id_component_type: 'moldura',
      code_changes_7days: 0,
      deploys_7days: 0,
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString()
    },
    {
      id: 'texto-element', 
      name: 'Texto',
      component_types: { 
        id: 'texto-type',
        name: 'Texto', 
        icon: null,
        group_name: 'Outros'
      },
      id_organization: 'system',
      id_component_type: 'texto',
      code_changes_7days: 0,
      deploys_7days: 0,
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString()
    },
    {
      id: 'ator-element',
      name: 'Ator',
      component_types: { 
        id: 'ator-type',
        name: 'Ator', 
        icon: null,
        group_name: 'Outros'
      },
      id_organization: 'system',
      id_component_type: 'ator',
      code_changes_7days: 0,
      deploys_7days: 0,
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString()
    }
  ];

  return (
    <>
      {sortedGroupNames.map((groupName) => (
        <ComponentGroup
          key={groupName}
          groupName={groupName}
          components={groupedComponents[groupName]}
          isExpanded={expandedGroups[groupName] ?? true}
          onToggle={() => onToggleGroup(groupName)}
          onDragStart={onDragStart}
        />
      ))}
      
      {/* Grupo fixo "Outros elementos" */}
      <ComponentGroup
        key="outros-elementos"
        groupName="Outros Elementos"
        components={fixedElements as Component[]}
        isExpanded={expandedGroups["outros-elementos"] ?? true}
        onToggle={() => onToggleGroup("outros-elementos")}
        onDragStart={onDragStart}
      />
    </>
  );
}
